import React from 'react'
import EventCard from './EventCard'
import LoadingSpinner from '../common/LoadingSpinner'

export default function EventList({
  events,
  user,
  searchCoordinates,
  loading,
  hasMore,
  onEventDeleted,
  isLastSection,
  lastEventElementRef
}) {
  // Ensure events is always an array
  const safeEvents = Array.isArray(events) ? events : []

  if (safeEvents.length === 0) {
    return (
      <div className="empty-state">
        <p className="empty-state-title">No upcoming events found.</p>
        <p className="empty-state-description">Try adjusting your search or check back later.</p>
      </div>
    )
  }

  // Calculate distance between two points using the Haversine formula
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3958.8 // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180
    const dLon = (lon2 - lon1) * Math.PI / 180
    const a =
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
    const distance = R * c
    return Math.round(distance * 10) / 10 // Round to 1 decimal place
  }

  const isEventCurrent = (event) => {
    const now = new Date()
    const startDate = new Date(event.start_date)
    const endDate = new Date(event.end_date)
    return startDate <= now && endDate >= now
  }

  const getDistanceText = (distance) => {
    if (distance === null || distance === undefined) return null
    return distance < 1 ? `${(distance * 5280).toFixed(0)} ft` : `${distance.toFixed(1)} mi`
  }

  // Separate user events from the rest
  const myEvents = safeEvents.filter(e => e.isUserEvent)
  const otherEvents = safeEvents.filter(e => !e.isUserEvent)

  // Group remaining events by distance (only when distances are available)
  const hasDistances = otherEvents.some(e => e.distance !== null && e.distance !== undefined)

  const groupedEvents = otherEvents.reduce((groups, event) => {
    const distance = event.distance
    let key

    if (!hasDistances || distance === null || distance === undefined) {
      key = 'all'
    } else if (distance < 1) {
      key = 'under1'
    } else if (distance < 5) {
      key = '1to5'
    } else if (distance < 10) {
      key = '5to10'
    } else {
      key = 'over10'
    }

    if (!groups[key]) groups[key] = []
    groups[key].push(event)
    return groups
  }, {})

  const sectionOrder = hasDistances
    ? ['under1', '1to5', '5to10', 'over10']
    : ['all']
  const sectionTitles = {
    under1: 'Within 1 mile',
    '1to5': '1-5 miles away',
    '5to10': '5-10 miles away',
    over10: '10+ miles away',
    all: 'Recently Added Events'
  }

  const hasOtherSections = sectionOrder.some(key => groupedEvents[key]?.length > 0)

  return (
    <div className="space-y-8">
      {/* User's events section - always shown first */}
      {myEvents.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-text border-b border-border pb-2">
            Your Events
          </h3>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {myEvents.map((event, eventIndex) => (
              <EventCard
                key={event.id}
                event={event}
                user={user}
                distance={getDistanceText(event.distance)}
                onEventDeleted={onEventDeleted}
                isLastInSection={eventIndex === myEvents.length - 1}
                lastEventElementRef={
                  isLastSection &&
                  !hasOtherSections &&
                  eventIndex === myEvents.length - 1
                    ? lastEventElementRef
                    : null
                }
              />
            ))}
          </div>
        </div>
      )}

      {sectionOrder.map((sectionKey, sectionIndex) => {
        const sectionEvents = groupedEvents[sectionKey]
        if (!sectionEvents || sectionEvents.length === 0) return null

        const isLastSectionWithEvents = sectionIndex === sectionOrder.length - 1 ||
          !sectionOrder.slice(sectionIndex + 1).some(key => groupedEvents[key]?.length > 0)

        return (
          <div key={sectionKey} className="space-y-4">
            <h3 className="text-lg font-semibold text-text border-b border-border pb-2">
              {sectionTitles[sectionKey]}
            </h3>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {sectionEvents.map((event, eventIndex) => (
                <EventCard
                  key={event.id}
                  event={event}
                  user={user}
                  distance={getDistanceText(event.distance)}
                  onEventDeleted={onEventDeleted}
                  isLastInSection={eventIndex === sectionEvents.length - 1}
                  lastEventElementRef={
                    isLastSection &&
                    isLastSectionWithEvents &&
                    eventIndex === sectionEvents.length - 1
                      ? lastEventElementRef
                      : null
                  }
                />
              ))}
            </div>
          </div>
        )
      })}

      {loading && (
        <div className="flex justify-center items-center py-8">
          <LoadingSpinner size="medium" />
        </div>
      )}

      {!loading && !hasMore && safeEvents.length > 0 && (
        <div className="text-center py-8 text-text-muted">
          No more events to load
        </div>
      )}
    </div>
  )
}
